// src/hr/portal/YearEndPage.js -- year-end close-out (spec §8.2, §16.2 M2; mockup screen 5 right card).
// GET /admin/year-end/:fromYear previews, per person and type, what is left, what to carry into next
// year, what to pay out and next year's allowance. Those server numbers are the defaults: nothing is
// recomputed here, and an untouched cell is sent back with the server's exact hours (cells show days but
// keep their hours; only typing changes them). Nothing changes until HR presses "Start <next year>"
// and confirms; when the close-out already started, the confirm says it overwrites and the POST
// carries confirmOverwrite: true.
// The checks below mirror the server's (validateYearEndRow) so Start can be disabled early; the
// server has the last word, and a refused POST marks every row it names.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { FiAlertCircle, FiArrowRight, FiCheck, FiInfo, FiRefreshCw } from 'react-icons/fi';
import { hr, errorText } from '../api';
import { Avatar, Btn, Card, EmptyState, Pill, Seg, Skeleton } from '../kit';
import { typeLabel, typeTone } from '../timeoff/labels';
import { daysBetween } from '../timeoff/dates';
import { daysCell, typedDaysCell } from './portalUtils';
import { FormError } from './FormError';

const TOLERANCE = 1e-6;
const SICK_CARRY_CAP_HOURS = 40;
const SICK_MIN_HOURS = 40;
// Within this many days of a new benefit year, the close-out still to do is last year's.
const CLOSE_LAST_YEAR_WITHIN_DAYS = 90;

const MSG = {
  NUMBER: 'Enter a number of days',
  NEGATIVE: "Hours can't be negative",
  SICK_CAP: "Sick carryover can't exceed 40 hours",
  FORFEIT: 'Unused sick leave must be carried over (up to 40 hours) or paid out',
  SICK_MIN: 'Sick & Safe must be at least 40 hours',
  NO_CARRY: "This type doesn't carry over",
  OVER_CARRY: "Can't carry over more than is left",
  OVER_PAYOUT: "Can't pay out more than is left"
};

const pairKey = (employeeId, typeId) => `${employeeId}|${typeId}`;
// Days with up to three decimals (an hour is 0.125 days), for read-only numbers.
const daysText = (hours) => String(Math.round(((Number(hours) || 0) / 8) * 1000) / 1000 || 0);

export function defaultFromYear(yearRange, today) {
  if (!yearRange || !yearRange.start) return Number(String(today).slice(0, 4));
  return daysBetween(yearRange.start, today) < CLOSE_LAST_YEAR_WITHIN_DAYS ? yearRange.year - 1 : yearRange.year;
}

function cellsFrom(preview) {
  const cells = {};
  ((preview && preview.rows) || []).forEach((row) => row.types.forEach((t) => {
    cells[pairKey(row.employeeId, t.typeId)] = {
      carry: daysCell(t.carryHours), payout: daysCell(t.payoutHours), next: daysCell(t.nextAllowanceHours)
    };
  }));
  return cells;
}

// The first problem with one row, the server's rules in the server's words.
function rowProblem(t, cell, type, left) {
  if (!cell) return null;
  const carry = cell.carry.hours;
  const payout = cell.payout.hours;
  const next = cell.next.hours;
  const left_ = Number(t.leftHours) || 0;
  if ([carry, payout, next].some((h) => !Number.isFinite(h))) return MSG.NUMBER;
  if (left) {
    if (payout < 0) return MSG.NEGATIVE;
    return payout > left_ + TOLERANCE ? MSG.OVER_PAYOUT : null;
  }
  if (t.isSick) {
    if (carry > SICK_CARRY_CAP_HOURS) return MSG.SICK_CAP;
    if (Math.abs(carry + payout - left_) > TOLERANCE) return MSG.FORFEIT;
    if (next < SICK_MIN_HOURS) return MSG.SICK_MIN;
    if (carry < 0 || payout < 0) return MSG.NEGATIVE;
    return null;
  }
  if (type && !type.carriesOver && carry > 0) return MSG.NO_CARRY;
  if (carry < 0 || payout < 0 || next < 0) return MSG.NEGATIVE;
  if (carry > left_) return MSG.OVER_CARRY;
  if (payout > left_ - carry + TOLERANCE) return MSG.OVER_PAYOUT;
  return null;
}

function useYearEnd(fromYear) {
  const [state, setState] = useState({ status: 'loading', data: null, year: fromYear, stamp: 0 });
  const latest = useRef(0);
  const stamp = useRef(0);
  const load = useCallback(async () => {
    latest.current += 1;
    const call = latest.current;
    setState((st) => ({ ...st, status: 'loading', data: null, year: fromYear }));
    try {
      const data = await hr.admin.yearEnd.get(fromYear);
      stamp.current += 1;
      if (call === latest.current) setState({ status: 'ready', data, year: fromYear, stamp: stamp.current });
    } catch (err) {
      if (call === latest.current) setState((st) => ({ ...st, status: 'error', data: null, year: fromYear, error: errorText(err, "Couldn't load the close-out.") }));
    }
  }, [fromYear]);
  useEffect(() => {
    load();
    return () => { latest.current += 1; };
  }, [load]);
  const set = useCallback((data) => {
    latest.current += 1;
    stamp.current += 1;
    setState({ status: 'ready', data, year: fromYear, stamp: stamp.current });
  }, [fromYear]);
  return { ...state, reload: load, set };
}

function NumberCell({ label, cell, onChange, invalid, errorId }) {
  return (
    <input
      type="number"
      className="hrp-input hrp-ye__num"
      step="any"
      min={0}
      inputMode="decimal"
      aria-label={label}
      aria-invalid={invalid ? 'true' : undefined}
      aria-describedby={invalid ? errorId : undefined}
      value={cell.text}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

function PersonRows({ row, fromYear, toYear, typesById, cells, problems, onCell }) {
  const types = row.types || [];
  const span = types.length + types.filter((t) => problems.get(pairKey(row.employeeId, t.typeId))).length;
  const personProblem = problems.get(pairKey(row.employeeId, ''));
  return (
    <tbody className="hrp-ye__grp">
      {types.map((t, i) => {
        const key = pairKey(row.employeeId, t.typeId);
        const type = typesById.get(t.typeId) || null;
        const name = type ? typeLabel(type) : t.isSick ? 'Sick & Safe' : t.key;
        const cell = cells[key];
        const problem = problems.get(key) || null;
        const errorId = `hrp-ye-err-${row.employeeId}-${t.typeId}`;
        const carries = t.isSick || (type ? type.carriesOver : Number(t.carryHours) > 0);
        const showCarry = !row.left && (carries || Number(t.carryHours) > 0);
        const showPayout = row.left || t.isSick || Number(t.payoutHours) > 0 || !!(t.existing && Number(t.existing.payoutHours) > 0);
        const showNext = !row.left;
        const label = (what) => `${row.name} ${name} ${what}`;
        const edit = (field) => (text) => onCell(key, field, text);
        const dash = (why) => <span className="hrp-ye__na" title={why}>—<span className="hrp-sr">{why}</span></span>;
        return (
          <React.Fragment key={t.typeId}>
            <tr data-invalid={problem ? 'true' : undefined}>
              {i === 0 && (
                <th scope="rowgroup" rowSpan={span} className="hrp-ye__who">
                  <div className="hrp-ye__whoin">
                    <Avatar id={row.employeeId} name={row.name} />
                    <div className="hrp-ye__whotxt">
                      <b>{row.name}</b>
                      {row.left && <span>{`Last day in ${fromYear} — payout only`}</span>}
                      {personProblem && <span className="hrp-ye__perr" role="alert">{personProblem}</span>}
                    </div>
                  </div>
                </th>
              )}
              <td data-label="Type" className="hrp-ye__type">
                <Pill {...typeTone(type || { key: t.key })} dot>{name}</Pill>
                {t.existing && (
                  <span className="hrp-ye__was" title={`Already written: carry ${daysText(t.existing.carryHours)}, paid out ${daysText(t.existing.payoutHours)}, allowance ${daysText(t.existing.nextAllowanceHours)} days`}>
                    <FiCheck aria-hidden="true" />Written
                    <span className="hrp-sr">{`: carry ${daysText(t.existing.carryHours)}, paid out ${daysText(t.existing.payoutHours)}, allowance ${daysText(t.existing.nextAllowanceHours)} days`}</span>
                  </span>
                )}
              </td>
              <td data-label="Left" className="hrp-ye__left">{daysText(t.leftHours)}</td>
              <td data-label={`Carry into ${toYear}`}>
                {showCarry
                  ? <NumberCell label={label(`carry into ${toYear}`)} cell={cell.carry} onChange={edit('carry')} invalid={!!problem} errorId={errorId} />
                  : dash(row.left ? 'Nothing carries over' : "Doesn't carry over")}
              </td>
              <td data-label="Paid out">
                {showPayout
                  ? <NumberCell label={label('paid out')} cell={cell.payout} onChange={edit('payout')} invalid={!!problem} errorId={errorId} />
                  : dash('Not paid out')}
              </td>
              <td data-label={`${toYear} allowance`}>
                {showNext
                  ? <NumberCell label={label(`${toYear} allowance`)} cell={cell.next} onChange={edit('next')} invalid={!!problem} errorId={errorId} />
                  : dash(`No ${toYear} allowance`)}
              </td>
            </tr>
            {problem && (
              <tr className="hrp-ye__errrow">
                <td colSpan={5}>
                  <p id={errorId} className="hrp-ye__err"><FiAlertCircle aria-hidden="true" />{problem}</p>
                </td>
              </tr>
            )}
          </React.Fragment>
        );
      })}
    </tbody>
  );
}

// The editable close-out for one preview. It is keyed by the preview, so a fresh preview (another
// year, a reload, the answer to a successful start) always begins from its own server defaults.
function CloseOut({ data, typesById, onStarted }) {
  const rows = data.rows || [];
  const { fromYear, toYear } = data;
  const defaults = useMemo(() => cellsFrom(data), [data]);
  const [cells, setCells] = useState(defaults);
  const [serverErrors, setServerErrors] = useState(new Map());
  const [started, setStarted] = useState(false); // a 409 said someone started it meanwhile
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const confirmRef = useRef(null);
  useEffect(() => {
    if (confirming && confirmRef.current) confirmRef.current.focus();
  }, [confirming]);

  const alreadyStarted = !!data.alreadyStarted || started;
  const people = `${rows.length} ${rows.length === 1 ? 'person' : 'people'}`;
  const clientProblems = new Map();
  rows.forEach((row) => row.types.forEach((t) => {
    const key = pairKey(row.employeeId, t.typeId);
    const problem = rowProblem(t, cells[key], typesById.get(t.typeId), row.left);
    if (problem) clientProblems.set(key, problem);
  }));
  const problems = new Map([...serverErrors, ...clientProblems]);
  const toFix = problems.size;
  const dirty = JSON.stringify(cells) !== JSON.stringify(defaults);

  const onCell = (key, field, text) => {
    setCells((c) => ({ ...c, [key]: { ...c[key], [field]: typedDaysCell(text) } }));
    if (serverErrors.has(key)) setServerErrors((m) => { const next = new Map(m); next.delete(key); return next; });
    setError('');
    setConfirming(false);
  };

  const start = async () => {
    if (busy) return;
    const body = {
      rows: rows.map((row) => ({
        employeeId: row.employeeId,
        types: row.types.map((t) => {
          const c = cells[pairKey(row.employeeId, t.typeId)];
          return { typeId: t.typeId, carryHours: c.carry.hours, payoutHours: c.payout.hours, nextAllowanceHours: c.next.hours };
        })
      }))
    };
    if (alreadyStarted) body.confirmOverwrite = true;
    setBusy(true);
    setError('');
    try {
      const fresh = await hr.admin.yearEnd.post(fromYear, body);
      onStarted(fresh, `${toYear} started — allowances written for ${people}`);
    } catch (err) {
      setBusy(false);
      setConfirming(false);
      const res = err && err.response;
      const list = res && res.data && Array.isArray(res.data.errors) ? res.data.errors : null;
      if (res && res.status === 400 && list && list.length) {
        setServerErrors(new Map(list.map((e) => [pairKey(e.employeeId, e.typeId || ''), e.error])));
      } else {
        if (res && res.status === 409) setStarted(true);
        setError(errorText(err, "Couldn't start the new year. Try again."));
      }
    }
  };

  const reset = () => {
    setCells(defaults);
    setServerErrors(new Map());
    setError('');
    setConfirming(false);
  };

  return (
    <>
      {alreadyStarted && (
        <div className="hrp-banner hrp-banner--info hrp-ye__started" role="note">
          <span className="hrp-banner__ic" aria-hidden="true"><FiCheck /></span>
          <div className="hrp-banner__txt">
            <div className="hrp-banner__title">{`${toYear} has already been started`}</div>
            <div className="hrp-banner__text">The numbers below include what was written. Starting again overwrites it.</div>
          </div>
        </div>
      )}
      <p className="hrp-ye__banner">
        Leftover days are filled in for you. Change any number you need to, then confirm. Nothing changes until you press <b>{`Start ${toYear}`}</b>.
      </p>
      <div className="hrp-table-wrap">
        <table className="hrp-table hrp-stack-table hrp-ye__table">
          <caption className="hrp-sr">{`Year-end close-out ${fromYear} → ${toYear}, in days`}</caption>
          <thead>
            <tr>
              <th scope="col">Person</th>
              <th scope="col">Type</th>
              <th scope="col">Left</th>
              <th scope="col">{`Carry into ${toYear}`}</th>
              <th scope="col">Paid out</th>
              <th scope="col">{`${toYear} allowance`}</th>
            </tr>
          </thead>
          {rows.map((row) => (
            <PersonRows key={row.employeeId} row={row} fromYear={fromYear} toYear={toYear} typesById={typesById} cells={cells} problems={problems} onCell={onCell} />
          ))}
        </table>
      </div>
      <p className="hrp-ye__rule">
        <FiInfo aria-hidden="true" />
        <span>In days (1 day = 8 hours). Sick &amp; Safe is never forfeited: what's left is carried over (up to 5 days) or paid out, and next year's Sick &amp; Safe is at least 5 days.</span>
      </p>

      <div className="hrp-ye__foot">
        <div className="hrp-ye__sum" aria-live="polite">
          {toFix > 0 && (
            <span data-tone="bad"><FiAlertCircle aria-hidden="true" />{`${toFix} ${toFix === 1 ? 'row needs' : 'rows need'} a fix — see the highlighted rows`}</span>
          )}
          {toFix === 0 && dirty && <span>Edited — nothing is saved until you start the new year.</span>}
          {toFix === 0 && !dirty && <span className="hrp-dim">These are the suggested numbers.</span>}
        </div>
        {confirming ? (
          <div className="hrp-ye__confirm" role="group" aria-label={`Start ${toYear}?`}>
            <p>
              {alreadyStarted
                ? `${toYear} was already started. This overwrites the ${toYear} allowances and carry-overs already written for ${people}, and the ${fromYear} payouts.`
                : `This writes the ${toYear} allowances and carry-overs for ${people} and records the ${fromYear} payouts. You can run it again later to change them.`}
            </p>
            <div className="hrp-ye__cbtns">
              <Btn ref={confirmRef} variant="ghost" size="sm" onClick={() => setConfirming(false)} disabled={busy}>Keep editing</Btn>
              <Btn size="sm" icon={FiArrowRight} loading={busy} onClick={start}>{alreadyStarted ? `Yes, overwrite ${toYear}` : `Yes, start ${toYear}`}</Btn>
            </div>
          </div>
        ) : (
          <div className="hrp-ye__btns">
            {dirty && <Btn variant="ghost" onClick={reset}>Reset</Btn>}
            <Link to=".." className="hrp-btn hrp-btn--ghost hrp-btn--md"><span className="hrp-btn__label">Cancel</span></Link>
            <Btn onClick={() => { setError(''); setConfirming(true); }} disabled={clientProblems.size > 0 || busy}>{`Start ${toYear}`}</Btn>
          </div>
        )}
        <FormError className="hrp-ye__ferr">{error}</FormError>
      </div>
    </>
  );
}

export function YearEndPage({ today, types, yearRange, onToast }) {
  const thisYear = yearRange && yearRange.year ? yearRange.year : Number(String(today).slice(0, 4));
  // ?year= (the People banner's link, R54) picks the year to close when it is one of the two offered.
  const [params] = useSearchParams();
  const asked = Number(params.get('year'));
  const [fromYear, setFromYear] = useState(() => (asked === thisYear - 1 || asked === thisYear
    ? asked
    : defaultFromYear(yearRange, today)));
  const preview = useYearEnd(fromYear);
  const data = preview.year === fromYear ? preview.data : null;
  const typesById = useMemo(() => new Map((types || []).map((t) => [t.id, t])), [types]);
  const rows = (data && data.rows) || [];
  const toYear = data ? data.toYear : fromYear + 1;

  const started = (fresh, text) => {
    onToast(text);
    if (fresh && Array.isArray(fresh.rows)) preview.set(fresh);
    else preview.reload();
  };

  let content;
  if (!data && preview.status === 'error') {
    content = (
      <EmptyState compact icon={FiAlertCircle} title="Couldn't load the close-out" action={<Btn variant="ghost" size="sm" onClick={preview.reload}>Try again</Btn>}>
        {preview.error || 'Something went wrong on our side.'}
      </EmptyState>
    );
  } else if (!data) {
    content = <Skeleton label="Loading the close-out" lines={6} height={22} />;
  } else if (rows.length === 0) {
    content = <EmptyState compact icon={FiRefreshCw} title={`Nobody to close out for ${fromYear}`}>People on HR's list in {fromYear} show up here with their leftover days.</EmptyState>;
  } else {
    content = <CloseOut key={preview.stamp} data={data} typesById={typesById} onStarted={started} />;
  }

  return (
    <div className="hrp-page hrp-ye">
      <div className="hrp-page-head">
        <div>
          <h1 className="hrp-h1">Year-end close-out</h1>
          <p className="hrp-sub">Carry leftover days into next year, record sick payouts and set next year's allowances.</p>
        </div>
        <div className="hrp-page-head__acts hrp-ye__years">
          <Seg
            label="Year to close"
            size="sm"
            options={[{ value: thisYear - 1, label: `${thisYear - 1} → ${thisYear}` }, { value: thisYear, label: `${thisYear} → ${thisYear + 1}` }]}
            value={fromYear}
            onChange={setFromYear}
          />
        </div>
      </div>

      <Card feat delay={0} as="section" aria-label={`Year-end close-out ${fromYear} to ${toYear}`} className="hrp-ye__card">
        <div className="hrp-ye__head">
          <div className="hrp-ye__yr">
            <span>Year-end close-out</span>
            <span className="hrp-ye__arrow">{`${fromYear} → ${toYear}`}</span>
          </div>
          {rows.length > 0 && <span className="hrp-ye__count">{`${rows.length} ${rows.length === 1 ? 'person' : 'people'}`}</span>}
        </div>
        {content}
      </Card>
    </div>
  );
}

export default YearEndPage;
